import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Select } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import type { QuickFilterOption } from './QuickFiltersSelect.types';

interface Props {
	options: QuickFilterOption[];
	value: Record<string, boolean | string>;
	onChange: (value: Record<string, boolean | string>) => void;
}

export const QuickFiltersSelect = ({ options, value, onChange }: Props) => {
	const activeOption = options.find((opt) => {
		if (!opt.filterKey) {
			// No filters are active
			return Object.keys(value).length === 0;
		}
		// Compare both key existence and value to correctly distinguish between options with same filterKey
		return value[opt.filterKey] === opt.filterValue;
	});

	const selectValue = activeOption?.value ?? options[0]?.value ?? null;

	const handleChange = (newValue: string | null) => {
		if (!newValue) {
			onChange({});
			return;
		}

		const option = options.find((opt) => opt.value === newValue);
		if (!option) {
			return;
		}

		// If option has no filterKey, clear all filters
		if (!option.filterKey) {
			onChange({});
			return;
		}

		onChange({ [option.filterKey]: option.filterValue as boolean | string });
	};

	return (
		<Select
			data-testid={TEST_ELEMENT_IDS.MANTINE_TABLE_QUICK_FILTER_SELECT}
			value={selectValue}
			onChange={handleChange}
			data={options.map((opt) => ({ value: opt.value, label: opt.label }))}
			placeholder="Quick filters"
			styles={{
				root: {
					width: '18.125rem',
					padding: '0 0.25rem',
					border: '1px solid transparent',
					borderLeftColor: defaultTheme.v3.colors.gray[2],
					display: 'flex',
					alignItems: 'center',
					'>div': {
						width: '100%',
					},
				},
				input: {
					height: '2.25rem',
					fontWeight: 600,
					fontSize: '0.875rem',
					'&&': { margin: 0 },
					'&:not(:disabled)': {
						'&:focus, &:hover, &.focus, &.hover': {
							borderColor: defaultTheme.v3.colors.gray[4],
						},
					},
				},
			}}
			h="100%"
			rightSection={
				<Icon variant="caret-down" size="lg" color={defaultTheme.v3.colors.dark[9]} />
			}
			rightSectionProps={{
				style: {
					pointerEvents: 'none',
					top: 0,
					height: '2.25rem',
					display: 'flex',
					alignItems: 'center',
				},
			}}
		/>
	);
};
